import React, { useContext, memo, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Cell, Row } from 'react-table';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import DataTable from '../../../DataTable/datatable.component';
import DateColumn from '../../../DataTable/datatable.datecolumn';
import NoFilter from '../../../DataTable/datatable.nofilter';
import SelectFilter from '../../../DataTable/datatable.selectfilter';
import { LabResult } from '../../../Types/LabResult';
import { LotDetailContext } from '../../../Contexts/LotDetailContext';
import { LabResultsColumnType } from './LabResultsSection';

interface LabResultsTableWrapperProps {
    cell: Cell<LabResult>,
    children: React.JSX.Element
}

function LabResultsRowWrapper({ cell, children }: LabResultsTableWrapperProps) {
    const { t } = useTranslation();
    if (cell === undefined || cell.row.original.usedInCalc) {
        return children;
    } else {
        return (
            <OverlayTrigger overlay={<Tooltip>{t("LotDetails.LabResults.UnusedInCalculation")}</Tooltip>} popperConfig={{}}>
                {children}
            </OverlayTrigger>
        );
    }
}

export interface LabResultsByQualityTableProps {
    isRailCarSite?: boolean,
    showSampleGrade?: boolean,
    children?: ReactNode,
    customColumns?: LabResultsColumnType[] 
}

const LabResultsByQualityTable = memo(function LabResultsByQualityTable({ isRailCarSite = false, showSampleGrade = false, children, customColumns = undefined }: LabResultsByQualityTableProps) {

    const { t } = useTranslation();
    const { lotDetail: { labResults } } = useContext(LotDetailContext);

    const defaultColumns: LabResultsColumnType[] = [
        "SampleTime",
        ...isRailCarSite ? [ "AuthorisedTime" as LabResultsColumnType, "BatchName" as LabResultsColumnType, "UnitArea" as LabResultsColumnType]: [],
        "Quality",
        "Analysis",
        "Component",
        "SamplingPoint",
        "SMSampleId",
        "Value",
        "Units",
        "Usage",
        ...showSampleGrade ? ["Grade" as LabResultsColumnType] : [],
    ];

    const labResultColumnTypeToColumn = (columnType: LabResultsColumnType): any => {
        switch (columnType) {
            case "SampleTime": 
                return DateColumn({ header: t('LotDetails.LabResults.SampleTime'), accessor: 'sampleTimeUtc' });
            case "AuthorisedTime":
                return DateColumn({ header: t('LotDetails.LabResults.AuthorisedTime'), accessor: 'authorisedTimeUtc' });
            case "BatchName":
                return { Header: t('LotDetails.LabResults.BatchName'), accessor: 'batchName', Filter: SelectFilter };
            case "UnitArea":
                return { Header: t('LotDetails.LabResults.UnitArea'), accessor: 'unitArea', Filter: SelectFilter };
            case "Quality":
                return { Header: t('LotDetails.LabResults.Quality'), accessor: 'quality', Filter: SelectFilter };
            case "Analysis":
                return { Header: t('LotDetails.LabResults.Analysis'), accessor: 'analysis', Filter: SelectFilter };
            case "Component":
                return { Header: t('LotDetails.LabResults.Component'), accessor: 'component', Filter: SelectFilter };
            case "SamplingPoint":
                return { Header: t('LotDetails.LabResults.SamplingPoint'), accessor: 'samplingPoint', Filter: SelectFilter }
            case "SMSampleId":
                return { Header: t('LotDetails.LabResults.SMSampleId'), accessor: 'smSampleId', filter: 'includesString' };
            case "Value":
                return { Header: t('LotDetails.LabResults.Value'), accessor: 'value', filter: 'includesString' };
            case "Units":
                return { Header: t('LotDetails.LabResults.Units'), accessor: 'units', Filter: NoFilter };
            case "Usage":
                return { Header: t('LotDetails.LabResults.Usage'), accessor: 'usage', Filter: NoFilter };
            case "Grade":
                return { Header: t('Grade'), accessor: 'grade', Filter: SelectFilter };
            case "Lot":
                return { Header: t('LotDetails.LabResults.Lot'), accessor: 'lotNumber', filter: 'includesString' };
        }
    }

    // The set of columns to actually display (may be customized)
    let labResultColumns = (customColumns && customColumns.length > 0) ? customColumns : defaultColumns;

    const tableColumns = labResultColumns.map(col => labResultColumnTypeToColumn(col));

    const getRowProps = (row: Row<LabResult>) => {
        const classNames: string[] = [];
        if (!row.original.usedInCalc) {
            classNames.push("unused");
        }

        if (row.original.isOffspec) {
            classNames.push("offspec");
        }

        if (classNames.length > 0) {
            return { className: classNames.join(' ') };
        }

        return {};
    };

    const initialState = {
        sortBy: [
            {
                id: 'sampleTimeUtc',
                desc: false
            },
            {
                id: 'quality',
                desc: false
            },
            {
                id: 'collection',
                desc: false
            }
        ],
        hiddenColumns: labResults.every(x => x.usage === null) ? ['usage'] : []
    };

    return labResults.length > 0
        ? <DataTable tableColumns={tableColumns} tableData={labResults} initialState={initialState} autoResetPage={false} getRowProps={getRowProps} CellWrapper={LabResultsRowWrapper}>
            {children}
        </DataTable>
        : <>
            <p>{t<string>('CommonResponses.NoData')}</p>
            {children}
        </>
        ;
});

export default LabResultsByQualityTable;